/* eslint-disable max-lines */
// eslint-disable-next-line @heidi/rules/must-lazy-load-modals -- pre-existing eager modal, surfaced when its drawer import moved to the door; lazy-load follow-up tracked separately
import appleIcon from '@/assets/logos/apple.svg';
import googleIcon from '@/assets/logos/google-color.svg';
import { termsCheckboxMessages } from '@/components/onboarding/fields/terms-checkbox/terms-checkbox.messages';
import { getConsentLegalLinks } from '@/components/utils/legalDocuments';
import { useWhiteLabelSafe } from '@/context/white-label';
import {
  IS_ONBOARDING_COMPLIANCE_DE_ENABLED,
  IS_ONBOARDING_COMPLIANCE_US_ENABLED,
  IS_SPECIALTY_IN_ORGANISATION_SURVEY_ENABLED,
  IS_SUCCINCT_WORK_EMAIL_HINT_ENABLED,
} from '@/features/flags/legacy-flags';
import { useTrackSignupPhoneFieldViewed } from '@/hooks/useTrackSignupPhoneFieldViewed';
import {
  signUpFlowStarted,
  signUpPhoneNumberChanged,
  trackSignUpFormCompleted,
} from '@/lib/analytics/scribe/sign-up';
import { trackFieldSelected, trackScreenViewed } from '@/lib/analytics/scribe/ui';
import { cn } from '@/lib/cn';
import { getProductImage } from '@/lib/utils';
import { countriesToRegion } from '@/lists/countriesToRegion';
import { AuthenticationProvider } from '@/models/analytics/features/onboarding';
import { isTauri, isUsingReactKinde } from '@/tauri/utils';
import { isAppleSignInAvailable } from '@/utils/appConfiguration';
import { getPhoneNumberVariantForCountry } from '@/utils/country-config';
import { isPersonalEmailDomain } from '@/utils/emailDomains';
import {
  hasLocalePathPrefix,
  isCjkLocale,
  SupportedLocale,
} from '@/utils/i18n/supportedLocales';
import { logger } from '@/utils/logger/logger';
import { Button } from '@heidi/design-system/button';
import { Checkbox } from '@heidi/design-system/checkbox';
import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import { Input } from '@heidi/design-system/input';
import { Link } from '@heidi/design-system/link';
import {
  DialogDrawer,
  DialogDrawerContent,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { toast } from '@heidi/design-system/toast';
import {
  TypographyCaption,
  TypographyCaptionBold,
  TypographyH2,
  TypographyH5,
  TypographyP2Bold,
  TypographyPBold,
} from '@heidi/design-system/typography';
import {
  baaRequiredAtom,
  clinicianTypeAtom,
  complianceKitRequiredAtom,
  countryAtom,
  displayLanguageAtom,
  firstNameAtom,
  lastNameAtom,
  otherClinicianTypeAtom,
  phoneNumberAtom,
} from '@heidi/growth/data';
import { FieldType, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { type LanguageCode } from '@heidi/schema/ml-scribe';
import { Loader } from '@heidi/snowflake-ui/loader';
import { countries } from '@heidi/utils/countries';
import {
  getAvailableDisplayLanguagesForCountry,
  shouldShowLanguageDropdown,
} from '@heidi/utils/i18n';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { useAtom } from 'jotai';
import { debounce, isEmpty } from 'lodash-es';
import { ArrowRight, Mail } from 'lucide-react';
import Image from 'next/image';
import NextLink from 'next/link';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl, type MessageDescriptor } from 'react-intl';
import { type Country } from 'react-phone-number-input';
import {
  KINDE_APPLE_CONNECTION_ID,
  KINDE_EMAIL_CONNECTION_ID,
  KINDE_GMAIL_CONNECTION_ID,
} from '../constants/kinde';
import { PhoneInput } from '../ui/phone-input';
import ClinicianTypeSelect, {
  OTHER_SPECIALTY_ID,
} from '../ui/select/clinician-type-select';
import CountrySelect from '../ui/select/country-select';
import DisplayLanguageSelect from '../ui/select/display-language-select';
import { sharedSignupMessages } from './shared-signup.messages';
import { signUpModalMessages } from './sign-up-modal.messages';
import { signupComplianceCheckboxesMessages } from './signup-compliance-checkboxes.messages';

interface Props {
  isOpen: boolean;
  setOpen?: (open: boolean) => void;
  showClinicianTypeSelect?: boolean;
  showCountrySelect?: boolean;
  showFirstName?: boolean;
  showLastName?: boolean;
  title?: string;
  subtitle?: string;
  logoVariant?: string;
  loginLinkVariant?: string;
  shouldBlurOverlay?: boolean;
  shouldShowLogin?: boolean;
  redirectTo?: string;
  trackSignUpFlowStarted?: boolean;
}
function SignUpModal(props: Props) {
  const {
    isOpen,
    setOpen,
    showClinicianTypeSelect,
    showCountrySelect,
    showFirstName,
    showLastName,
    title,
    subtitle,
    logoVariant,
    loginLinkVariant,
    shouldBlurOverlay,
    shouldShowLogin,
    redirectTo,
    trackSignUpFlowStarted,
  } = props;

  const intl = useIntl();
  const locale = intl.locale as SupportedLocale;
  const isFrenchLocale = locale === SupportedLocale.FrFr;
  const isFullHeightLocale =
    locale === SupportedLocale.DeDe || locale === SupportedLocale.FrFr;
  const legalLinks = getConsentLegalLinks(locale);
  const { register } = useKindeAuth();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useAtom(firstNameAtom);
  const [lastName, setLastName] = useAtom(lastNameAtom);
  const [country, setCountry] = useAtom(countryAtom);
  const [clinicianType, setClinicianType] = useAtom(clinicianTypeAtom);
  const [otherClinicianType, setOtherClinicianType] = useAtom(otherClinicianTypeAtom);
  const [phoneNumber, setPhoneNumber] = useAtom(phoneNumberAtom);
  const [displayLanguage, setDisplayLanguage] = useAtom(displayLanguageAtom);
  const [baaRequired, setBaaRequired] = useAtom(baaRequiredAtom);
  const [complianceKitRequired, setComplianceKitRequired] = useAtom(
    complianceKitRequiredAtom
  );
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [isTermsError, setIsTermsError] = useState(false);

  // Debounced field_selected trackers for text inputs
  const debouncedTrackFirstName = useMemo(
    () =>
      debounce(() => {
        trackFieldSelected({
          fieldName: 'first_name',
          fieldType: FieldType.Text,
          screenName: ScreenName.SignupV1,
        });
      }, 500),
    []
  );

  const debouncedTrackLastName = useMemo(
    () =>
      debounce(() => {
        trackFieldSelected({
          fieldName: 'last_name',
          fieldType: FieldType.Text,
          screenName: ScreenName.SignupV1,
        });
      }, 500),
    []
  );

  const debouncedTrackEmail = useMemo(
    () =>
      debounce(() => {
        trackFieldSelected({
          fieldName: 'email',
          fieldType: FieldType.Text,
          screenName: ScreenName.SignupV1,
        });
      }, 500),
    []
  );

  const debouncedTrackPhoneNumber = useMemo(
    () =>
      debounce(() => {
        trackFieldSelected({
          fieldName: 'phone_number',
          fieldType: FieldType.Text,
          screenName: ScreenName.SignupV1,
        });
      }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedTrackFirstName.cancel();
      debouncedTrackLastName.cancel();
      debouncedTrackEmail.cancel();
      debouncedTrackPhoneNumber.cancel();
    };
  }, [
    debouncedTrackFirstName,
    debouncedTrackLastName,
    debouncedTrackEmail,
    debouncedTrackPhoneNumber,
  ]);

  const hasTrackedSignUpFlow = useRef(false);

  useEffect(() => {
    if (isOpen && trackSignUpFlowStarted && !hasTrackedSignUpFlow.current) {
      hasTrackedSignUpFlow.current = true;
      signUpFlowStarted();
      trackScreenViewed({
        screen_name: ScreenName.SignupV1,
      });
    }
    if (!isOpen) {
      hasTrackedSignUpFlow.current = false;
    }
  }, [isOpen, trackSignUpFlowStarted]);

  // Field change handlers with tracking
  const handleFirstNameChange = useCallback(
    (value: string) => {
      setFirstName(value);
      if (value) {
        debouncedTrackFirstName();
      }
      if (country) {
        setCountry(country);
      }
    },
    [setFirstName, debouncedTrackFirstName, country, setCountry]
  );

  const handleLastNameChange = useCallback(
    (value: string) => {
      setLastName(value);
      if (value) {
        debouncedTrackLastName();
      }
      if (country) {
        setCountry(country);
      }
    },
    [setLastName, debouncedTrackLastName, country, setCountry]
  );

  const handleEmailChange = useCallback(
    (value: string) => {
      setEmail(value);
      if (value) {
        debouncedTrackEmail();
      }
      if (country) {
        setCountry(country);
      }
    },
    [debouncedTrackEmail, country, setCountry]
  );

  const handleClinicianTypeChange = useCallback(
    (value: typeof clinicianType) => {
      setClinicianType(value);
      trackFieldSelected({
        fieldName: 'specialty',
        fieldType: FieldType.Dropdown,
        screenName: ScreenName.SignupV1,
      });
    },
    [setClinicianType]
  );

  const handleCountryChange = useCallback(
    (value: typeof country) => {
      setCountry(value);
      // Auto-select the first available language for the new country
      const availableLangs = getAvailableDisplayLanguagesForCountry(value?.countryCode);
      if (availableLangs.length > 0) {
        setDisplayLanguage(availableLangs[0]);
      } else {
        setDisplayLanguage(undefined);
      }
      trackFieldSelected({
        fieldName: 'country',
        fieldType: FieldType.Dropdown,
        screenName: ScreenName.SignupV1,
      });
    },
    [setCountry, setDisplayLanguage]
  );

  const handleDisplayLanguageChange = useCallback(
    (value: LanguageCode) => {
      setDisplayLanguage(value);
      trackFieldSelected({
        fieldName: 'display_language',
        fieldType: FieldType.Dropdown,
        screenName: ScreenName.SignupV1,
      });
    },
    [setDisplayLanguage]
  );

  const availableDisplayLanguages = useMemo(
    () => getAvailableDisplayLanguagesForCountry(country?.countryCode),
    [country?.countryCode]
  );

  const showDisplayLanguageSelect = useMemo(
    () => shouldShowLanguageDropdown(country?.countryCode),
    [country?.countryCode]
  );

  const handlePhoneNumberChange = useCallback(
    (value: string) => {
      setPhoneNumber(value);
      signUpPhoneNumberChanged({
        country: country?.countryCode ?? '',
        phoneNumber: value,
      });
      if (value) {
        debouncedTrackPhoneNumber();
      }
    },
    [setPhoneNumber, country?.countryCode, debouncedTrackPhoneNumber]
  );

  const handleBaaRequiredChange = useCallback(
    (checked: boolean) => {
      setBaaRequired(checked);
      trackFieldSelected({
        fieldName: 'baa_required',
        fieldType: FieldType.Checkbox,
        screenName: ScreenName.SignupV1,
      });
    },
    [setBaaRequired]
  );

  const handleComplianceKitRequiredChange = useCallback(
    (checked: boolean) => {
      setComplianceKitRequired(checked);
      trackFieldSelected({
        fieldName: 'compliance_kit_required',
        fieldType: FieldType.Checkbox,
        screenName: ScreenName.SignupV1,
      });
    },
    [setComplianceKitRequired]
  );

  const handleTermsCheckedChange = useCallback((checked: boolean) => {
    setIsTermsChecked(checked);
    if (checked) setIsTermsError(false);
    trackFieldSelected({
      fieldName: 'terms_accepted',
      fieldType: FieldType.Checkbox,
      screenName: ScreenName.SignupV1,
    });
  }, []);

  const allowedCountries = useMemo(() => {
    return countries
      .filter((country) => country.countryCode)
      .map((country) => country.countryCode as Country);
  }, []);

  const countryIsUS = country?.countryCode === 'US';
  const countryIsDE = country?.countryCode === 'DE';

  const phoneNumberVariant = getPhoneNumberVariantForCountry(country?.countryCode);
  const showPhoneNumber =
    showCountrySelect &&
    IS_SPECIALTY_IN_ORGANISATION_SURVEY_ENABLED() &&
    phoneNumberVariant !== 'hidden';
  const isPhoneNumberOptional = phoneNumberVariant === 'optional';

  useTrackSignupPhoneFieldViewed({
    isVisible: Boolean(showPhoneNumber),
    isOptional: isPhoneNumberOptional,
    surface: 'signup_modal',
    countryCode: country?.countryCode,
  });

  const showBaaCheckbox =
    IS_ONBOARDING_COMPLIANCE_US_ENABLED() && showCountrySelect && countryIsUS;

  const showComplianceKitCheckbox =
    IS_ONBOARDING_COMPLIANCE_DE_ENABLED() && showCountrySelect && countryIsDE;

  const disableButton =
    isEmpty(email) ||
    (showClinicianTypeSelect && isEmpty(clinicianType)) ||
    (showClinicianTypeSelect &&
      clinicianType?.id === OTHER_SPECIALTY_ID &&
      isEmpty(otherClinicianType)) ||
    (showCountrySelect && isEmpty(country)) ||
    (showFirstName && isEmpty(firstName)) ||
    (showLastName && isEmpty(lastName)) ||
    (showPhoneNumber && !isPhoneNumberOptional && isEmpty(phoneNumber));

  const generatePostSignupRedirectUri = () => {
    // If redirectTo is explicitly provided, use it (with locale prefix if not already present)
    if (redirectTo) {
      // Check if redirectTo already has a locale prefix
      const hasLocalePrefix = hasLocalePathPrefix(redirectTo);
      return hasLocalePrefix ? redirectTo : `/${locale}${redirectTo}`;
    }

    // add hashes to the end of the url
    if (typeof window !== 'undefined') {
      const selectedCountry = country;
      const region = selectedCountry?.name
        ? countriesToRegion[selectedCountry.name]
        : undefined;

      const hash = window?.location.hash;
      const currentSearch = window?.location.search || '';
      const searchParams = new URLSearchParams(currentSearch);

      if (region) {
        searchParams.set('region', region);
      }

      const query = searchParams.toString() ? `?${searchParams.toString()}` : ''; // Rebuild query string

      const hashWithoutIsDemo = hash?.replace(/demo=true&?/, '');
      if (hashWithoutIsDemo) {
        logger.debug('hashWithoutIsDemo', hashWithoutIsDemo);
        if (isTauri()) {
          return `${window.location.origin}/${locale}/scribe/new-user${query}${hashWithoutIsDemo}`;
        }
        return `/${locale}/scribe/new-user${query}${hashWithoutIsDemo}`;
      }
    }

    return `/${locale}/scribe/new-user`;
  };

  // add a click state to show loader when sign in button is clicked
  const [clicked, setClicked] = useState<'email' | 'google' | 'microsoft' | 'apple'>();

  const isPersonalEmail = isPersonalEmailDomain(email);
  const { isWhiteLabel, partnerName } = useWhiteLabelSafe();

  const titleMessage = useMemo((): {
    descriptor: MessageDescriptor;
    values?: { partnerName: string };
  } => {
    if (isWhiteLabel && partnerName) {
      return {
        descriptor: signUpModalMessages.joinPartnerTitle,
        values: { partnerName },
      };
    }
    switch (title) {
      case 'variant-1':
        return { descriptor: signUpModalMessages.titleVariant1 };
      case 'variant-2':
        return { descriptor: signUpModalMessages.titleVariant2 };
      case 'variant-3':
        return { descriptor: signUpModalMessages.titleVariant3 };
      case 'variant-4':
        return { descriptor: signUpModalMessages.titleVariant4 };
      case 'control':
      default:
        return { descriptor: signUpModalMessages.titleDefault };
    }
  }, [isWhiteLabel, partnerName, title]);

  const subtitleDescriptor = useMemo((): MessageDescriptor | null => {
    switch (subtitle) {
      case 'variant-1':
        return signUpModalMessages.subtitleVariant1;
      case 'variant-2':
        return signUpModalMessages.subtitleVariant2;
      case 'variant-3':
        return signUpModalMessages.subtitleVariant3;
      case 'variant-4':
        return signUpModalMessages.subtitleVariant4;
      case 'removed':
        return null;
      case 'control':
      default:
        return signUpModalMessages.subtitleDefault;
    }
  }, [subtitle]);

  const finalLogo = (() => {
    switch (logoVariant) {
      case 'removed':
        return false;
      case 'control':
      default:
        return true;
    }
  })();

  const finalLoginLink = (() => {
    switch (loginLinkVariant) {
      case 'removed':
        return false;
      case 'control':
      default:
        return true;
    }
  })();

  const productImage = getProductImage();

  const firstNameField = showFirstName ? (
    <div key="firstName" className="flex w-full flex-col gap-1">
      <TypographyP2Bold className="leading-tight">
        <FormattedMessage {...signUpModalMessages.firstNameLabel} />
      </TypographyP2Bold>
      <Input
        data-testid="sign-up-firstName"
        value={firstName}
        onChange={(e) => handleFirstNameChange(e.target.value)}
        className="w-full"
        placeholder={intl.formatMessage(signUpModalMessages.firstNamePlaceholder)}
        type="firstname"
        autoComplete="given-name"
      />
    </div>
  ) : null;

  const lastNameField = showLastName ? (
    <div key="lastName" className="flex w-full flex-col gap-1">
      <TypographyP2Bold className="leading-tight">
        <FormattedMessage {...signUpModalMessages.lastNameLabel} />
      </TypographyP2Bold>
      <Input
        data-testid="sign-up-lastName"
        value={lastName}
        onChange={(e) => handleLastNameChange(e.target.value)}
        placeholder={intl.formatMessage(signUpModalMessages.lastNamePlaceholder)}
        className="w-full"
        type="lastname"
        autoComplete="family-name"
      />
    </div>
  ) : null;

  const nameFields = isCjkLocale(locale)
    ? [lastNameField, firstNameField]
    : [firstNameField, lastNameField];

  return (
    <DialogDrawer open={isOpen} onOpenChange={setOpen} drawerRepositionInputs={false}>
      <DialogDrawerContent
        className={cn(
          'flex flex-col overflow-y-auto p-8 sm:w-11/12 sm:max-w-lg sm:overflow-y-hidden',
          isFullHeightLocale ? 'max-h-screen' : 'max-h-[90vh]'
        )}
        hideCloseIcon
        // Allow callers (e.g. SignUpPromptModalChat) to disable any blur entirely
        overlayClassName={cn(shouldBlurOverlay && productImage)}
        data-testid="sign-up-modal"
      >
        <div className="flex h-full flex-col gap-4 overflow-y-auto">
          <div className="flex w-full flex-col justify-center gap-4">
            {finalLogo && (
              <HeidiLogoMark
                data-testid="brand-icon"
                className="size-[42px] self-center"
                artwork="standard"
              />
            )}
            <div>
              {isFrenchLocale ? (
                <TypographyH5 className="w-full p-0 text-center text-[36px]">
                  <FormattedMessage
                    {...titleMessage.descriptor}
                    values={titleMessage.values}
                  />
                </TypographyH5>
              ) : (
                <TypographyH2 className="w-full p-0 text-center">
                  <FormattedMessage
                    {...titleMessage.descriptor}
                    values={titleMessage.values}
                  />
                </TypographyH2>
              )}
              {subtitleDescriptor ? (
                <TypographyPBold className="mt-1.5 w-full text-center text-text-secondary">
                  <FormattedMessage {...subtitleDescriptor} />
                </TypographyPBold>
              ) : null}
            </div>
          </div>
          <div className="flex w-full gap-3">{nameFields}</div>
          <div className="flex w-full flex-col gap-1">
            <TypographyP2Bold className="leading-tight">
              <FormattedMessage {...signUpModalMessages.workEmailLabel} />
            </TypographyP2Bold>
            <Input
              data-testid="sign-up-email"
              required
              accessoryStart={<Mail className="size-4" />}
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              className={cn('w-[406px]')}
              wrapperClassName={cn(
                isPersonalEmail &&
                  'border-validation-warning focus-visible:ring-validation-warning'
              )}
              placeholder={intl.formatMessage(signUpModalMessages.workEmailPlaceholder)}
              type="email"
              autoComplete="email"
            />
            <TypographyCaption
              className={cn(
                'text-text-secondary',
                isPersonalEmail &&
                  (IS_SUCCINCT_WORK_EMAIL_HINT_ENABLED()
                    ? 'text-text-secondary'
                    : 'text-validation-warning')
              )}
            >
              {IS_SUCCINCT_WORK_EMAIL_HINT_ENABLED() ? (
                <FormattedMessage {...signUpModalMessages.succinctWorkEmailHint} />
              ) : (
                <FormattedMessage {...signUpModalMessages.workEmailHint} />
              )}
            </TypographyCaption>
          </div>

          {showClinicianTypeSelect && !IS_SPECIALTY_IN_ORGANISATION_SURVEY_ENABLED() && (
            <div className="flex w-full flex-col gap-1">
              <TypographyP2Bold className="leading-tight">
                <FormattedMessage {...signUpModalMessages.specialtyLabel} />
              </TypographyP2Bold>
              <ClinicianTypeSelect
                selectedSpecialty={clinicianType}
                setSelectedSpecialty={handleClinicianTypeChange}
                otherSpecialty={otherClinicianType}
                setOtherSpecialty={setOtherClinicianType}
              />
            </div>
          )}
          {showCountrySelect && (
            <div className="flex w-full flex-col gap-1">
              <TypographyP2Bold className="leading-tight">
                <FormattedMessage {...signUpModalMessages.countryLabel} />
              </TypographyP2Bold>
              <CountrySelect value={country} setValue={handleCountryChange} />
            </div>
          )}

          {showCountrySelect && showDisplayLanguageSelect && (
            <div className="flex w-full flex-col gap-1">
              <TypographyP2Bold className="leading-tight">
                <FormattedMessage {...signUpModalMessages.displayLanguageLabel} />
              </TypographyP2Bold>
              <DisplayLanguageSelect
                value={displayLanguage}
                setValue={handleDisplayLanguageChange}
                availableLanguages={availableDisplayLanguages}
              />
            </div>
          )}

          {showPhoneNumber && (
            <div className="flex w-full flex-col gap-1">
              {isPhoneNumberOptional ? (
                <TypographyP2Bold className="leading-tight">
                  <FormattedMessage {...signUpModalMessages.phoneNumberOptional} />
                </TypographyP2Bold>
              ) : (
                <div className="flex gap-0.5">
                  <TypographyP2Bold className="leading-tight">
                    <FormattedMessage {...signUpModalMessages.phoneNumberLabel} />
                  </TypographyP2Bold>
                  <TypographyP2Bold className="text-validation-destructive" aria-hidden>
                    {intl.formatMessage(sharedSignupMessages.requiredFieldMarker)}
                  </TypographyP2Bold>
                </div>
              )}
              <PhoneInput
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                allowedCountries={allowedCountries}
                defaultCountry={country?.countryCode as Country}
                placeholder={intl.formatMessage(
                  signUpModalMessages.phoneNumberPlaceholder
                )}
              />
            </div>
          )}

          {showBaaCheckbox && (
            <div className="flex w-full flex-row items-center gap-2">
              <Checkbox
                id="baa-checkbox"
                checked={baaRequired}
                onCheckedChange={handleBaaRequiredChange}
              />
              <TypographyCaptionBold className="text-text-secondary">
                <FormattedMessage {...signupComplianceCheckboxesMessages.baaCheckbox} />
              </TypographyCaptionBold>
            </div>
          )}

          {showComplianceKitCheckbox && (
            <div className="flex w-full flex-row items-center gap-2">
              <Checkbox
                id="compliance-kit-checkbox"
                checked={complianceKitRequired}
                onCheckedChange={handleComplianceKitRequiredChange}
              />
              <TypographyCaptionBold className="text-text-secondary">
                <FormattedMessage
                  {...signupComplianceCheckboxesMessages.complianceKitCheckbox}
                />
              </TypographyCaptionBold>
            </div>
          )}
          <div className="flex w-full flex-row items-center gap-2">
            <Checkbox
              id="terms-checkbox"
              checked={isTermsChecked}
              onCheckedChange={handleTermsCheckedChange}
            />
            <TypographyCaptionBold
              className={cn(
                'text-text-secondary',
                isTermsError && 'text-validation-destructive'
              )}
            >
              <FormattedMessage
                {...termsCheckboxMessages.termsAgreementWithLinks}
                values={{
                  usagePolicy: (chunks) => (
                    <Link
                      href={legalLinks.usagePolicy}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="inline"
                    >
                      {chunks}
                    </Link>
                  ),
                  privacyPolicy: (chunks) => (
                    <Link
                      href={legalLinks.privacyPolicy}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="inline"
                    >
                      {chunks}
                    </Link>
                  ),
                  and: (chunks) => chunks,
                  termsOfUse: (chunks) => (
                    <Link
                      href={legalLinks.termsOfUse}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="inline"
                    >
                      {chunks}
                    </Link>
                  ),
                }}
              />
            </TypographyCaptionBold>
          </div>
          <div className="flex w-full flex-col gap-3">
            {disableButton ? (
              <Button
                className="w-full"
                size="lg"
                disabled={disableButton}
                variant="default"
              >
                {clicked === 'email' ? (
                  <Loader className="size-6 text-text-primary-invert" />
                ) : (
                  <FormattedMessage {...signUpModalMessages.joinForFree} />
                )}
              </Button>
            ) : isUsingReactKinde() ? (
              <Button
                disableAutomaticSvgSizing
                size="lg"
                disabled={disableButton}
                onClick={() => {
                  setClicked('email');

                  void register({
                    authUrlParams: {
                      login_hint: email,
                      firstName: firstName,
                      lastName: lastName,
                      post_login_redirect_url: generatePostSignupRedirectUri(),
                      connection_id: KINDE_EMAIL_CONNECTION_ID,
                      lang: locale,
                    },
                    app_state: {
                      redirectTo: generatePostSignupRedirectUri(),
                    },
                  });

                  trackSignUpFormCompleted({
                    authenticationProvider: AuthenticationProvider.EMAIL,
                  });
                }}
              >
                {clicked === 'email' ? (
                  <Loader className="size-6 text-text-primary-invert" />
                ) : (
                  <FormattedMessage {...signUpModalMessages.signInWithEmail} />
                )}
              </Button>
            ) : (
              <NextLink
                className="w-full"
                href={{
                  pathname: '/api/auth/register',
                  query: {
                    login_hint: email,
                    firstName: firstName,
                    lastName: lastName,
                    post_login_redirect_url: generatePostSignupRedirectUri(),
                    connection_id: KINDE_EMAIL_CONNECTION_ID,
                  },
                }}
              >
                <Button
                  size="lg"
                  variant="default"
                  className="w-full"
                  disabled={disableButton}
                  onClick={(e) => {
                    if (!isTermsChecked) {
                      e.preventDefault();
                      setIsTermsError(true);
                      toast({
                        tone: 'destructive',
                        title: intl.formatMessage(signUpModalMessages.termsRequiredTitle),
                        description: intl.formatMessage(
                          signUpModalMessages.termsRequiredDescription
                        ),
                        duration: 3000,
                      });
                      return;
                    }

                    setClicked('email');

                    trackSignUpFormCompleted({
                      authenticationProvider: AuthenticationProvider.EMAIL,
                    });
                  }}
                >
                  {clicked === 'email' ? (
                    <Loader className="size-6 text-text-primary-invert" />
                  ) : (
                    <FormattedMessage {...signUpModalMessages.joinForFree} />
                  )}
                </Button>
              </NextLink>
            )}
            {!navigator?.userAgent?.includes('FBAV') &&
              !navigator?.userAgent?.includes('Instagram') &&
              (isUsingReactKinde() ? (
                <>
                  <Button
                    disableAutomaticSvgSizing
                    size="lg"
                    className="w-full"
                    variant="secondary"
                    onClick={() => {
                      setClicked('google');

                      void register({
                        authUrlParams: {
                          connection_id: KINDE_GMAIL_CONNECTION_ID,
                          post_login_redirect_url: generatePostSignupRedirectUri(),
                          lang: locale,
                        },
                        app_state: {
                          redirectTo: generatePostSignupRedirectUri(),
                        },
                      });

                      trackSignUpFormCompleted({
                        authenticationProvider: AuthenticationProvider.GOOGLE,
                      });
                    }}
                  >
                    <Image
                      src={googleIcon}
                      loading="lazy"
                      alt={intl.formatMessage(signUpModalMessages.googleLogoAlt)}
                      className="size-4.5"
                    />
                    <span>
                      <FormattedMessage {...signUpModalMessages.signUpWithGoogle} />
                    </span>
                  </Button>

                  {isAppleSignInAvailable() && (
                    <Button
                      disableAutomaticSvgSizing
                      size="lg"
                      variant="secondary"
                      className="w-full"
                      onClick={() => {
                        setClicked('apple');

                        void register({
                          authUrlParams: {
                            connection_id: KINDE_APPLE_CONNECTION_ID,
                            post_login_redirect_url: generatePostSignupRedirectUri(),
                            lang: locale,
                          },
                          app_state: {
                            redirectTo: generatePostSignupRedirectUri(),
                          },
                        });

                        trackSignUpFormCompleted({
                          authenticationProvider: AuthenticationProvider.APPLE,
                        });
                      }}
                    >
                      <Image
                        src={appleIcon}
                        loading="lazy"
                        alt={intl.formatMessage(signUpModalMessages.appleLogoAlt)}
                        className="dark-invert size-4.5"
                      />
                      <span>
                        <FormattedMessage {...signUpModalMessages.signUpWithApple} />
                      </span>
                    </Button>
                  )}
                </>
              ) : (
                <NextLink
                  className="w-full"
                  href={{
                    pathname: '/api/auth/register',
                    query: {
                      post_login_redirect_url: generatePostSignupRedirectUri(),
                      connection_id: KINDE_GMAIL_CONNECTION_ID,
                    },
                  }}
                >
                  <Button
                    disableAutomaticSvgSizing
                    size="lg"
                    variant="secondary"
                    className="w-full"
                    onClick={() => {
                      setClicked('google');

                      trackSignUpFormCompleted({
                        authenticationProvider: AuthenticationProvider.GOOGLE,
                      });
                    }}
                  >
                    <Image
                      src={googleIcon}
                      loading="lazy"
                      alt={intl.formatMessage(signUpModalMessages.googleLogoAlt)}
                      className="size-4.5"
                    />
                    <span>
                      <FormattedMessage {...signUpModalMessages.signUpWithGoogle} />
                    </span>
                  </Button>
                </NextLink>
              ))}
            {finalLoginLink && shouldShowLogin && (
              <Button asChild variant="ghost" size="lg" className="w-full">
                <NextLink href="/">
                  <span>
                    <FormattedMessage {...signUpModalMessages.alreadyHaveAccount} />
                  </span>
                  <ArrowRight className="rtl:-scale-x-100" />
                </NextLink>
              </Button>
            )}
          </div>
        </div>
      </DialogDrawerContent>
    </DialogDrawer>
  );
}

export default SignUpModal;
